import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, throwError, timer, timeout, firstValueFrom } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';

import { APP_CONFIG } from "../../../config/config";
import { AppConfig } from "../../../config/config.type";

export type UploadStatus = 'preparing' | 'uploading' | 'processing' | 'finalizing' | 'paused' | 'completed' | 'error' | 'cancelled';

export interface ChunkInfo {
  chunkNumber: number;
  start: number;
  end: number;
  size: number;
  uploadId: string;
  etag?: string;
}

export interface UploadSession {
  uploadId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: ChunkInfo[];
  status: UploadStatus;
  entityName: string;
  entityId: string;
  isCover: boolean;
  fileType?: 'teaser' | 'cover-image' | 'booklet' | 'booklet-thumbnail';
  uploadUrl?: string;
  cancelSubject?: Subject<void>;
  startTime?: number;
  lastProgressTime?: number;
  lastProgressBytes?: number;
  uploadSpeed?: number;
  eta?: number;
}

@Injectable({
  providedIn: 'root'
})
export class UploadLargeFileService {
  private readonly apiUrl: string;

  private readonly chunkSize = 5 * 1024 * 1024; // 1MB chunks (temporary for testing)

  private readonly maxRetries = 3;

  private readonly uploadSessions = new Map<string, UploadSession>();

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly http: HttpClient
  ) {
    this.apiUrl = this.appConfig.apiUrl;
  }

  uploadLargeFile(
    file: File,
    entityName: 'my-profile' | 'thematics' | 'contents' | 'thematics-details' | 'formation-contents' | 'formations',
    id = '',
    isCover = false,
    fileType?: 'teaser' | 'cover-image' | 'booklet' | 'booklet-thumbnail'
  ): Observable<HttpEvent<any>> {
    // For content creation (no ID), don't upload immediately - wait for content creation
    if ((entityName === 'contents' || entityName === 'formation-contents') && (!id || id.trim().length === 0)) {
      throw new Error('Content ID is required for file upload. Create content first.');
    }

    // For existing content, use multipart for files >= 50MB, otherwise regular upload
    const shouldUseRegularUpload = file.size < 50 * 1024 * 1024; // 50MB threshold

    if (shouldUseRegularUpload) {
      return this.regularUpload(file, entityName, id, isCover, fileType);
    }

    return this.multipartUpload(file, entityName, id, isCover, fileType);
  }

  private regularUpload(
    file: File,
    entityName: string,
    id: string,
    isCover: boolean,
    fileType?: 'teaser' | 'cover-image' | 'booklet' | 'booklet-thumbnail'
  ): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append("file", file);

    if (isCover) {
      formData.append("contentFileType", 'cover');
    } else {
      // Determine content file type based on file MIME type
      const contentFileType = this.getContentFileType(file);
      formData.append("contentFileType", contentFileType);
    }

    let url: string;

    // Handle different scenarios for contents
    if (entityName === 'contents') {
      if (id && id.trim().length > 0) {
        // Existing content - use the existing endpoint
        url = `${this.apiUrl}/contents/${id}/image`;
      } else {
        // No ID provided - use standalone upload endpoint
        url = `${this.apiUrl}/contents/upload-file`;
      }
    } else if (entityName === 'formation-contents') {
      // Formation contents use /media endpoint instead of /image
      url = `${this.apiUrl}/formation-contents/${id}/media`;
    } else if (entityName === 'formations' && fileType) {
      // Formations use specific endpoints based on file type
      url = `${this.apiUrl}/formations/${id}/${fileType}`;
    } else {
      // For other entities (thematics, profile, etc.)
      const urlParts = [this.apiUrl, entityName];
      if (id && id.trim().length > 0) {
        urlParts.push(id);
      }
      urlParts.push('image');
      url = urlParts.join('/');
    }

    const req = new HttpRequest('POST', url, formData, {
      reportProgress: true
    });

    const startTime = Date.now();
    let lastProgressTime = startTime;
    let lastProgressBytes = 0;

    return this.http.request(req).pipe(
      tap(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const now = Date.now();
          const timeDiff = (now - lastProgressTime) / 1000; // seconds
          const bytesDiff = event.loaded - lastProgressBytes;

          if (timeDiff > 0.5) { // Update speed every 500ms
            const speed = bytesDiff / timeDiff; // bytes per second
            const remainingBytes = event.total - event.loaded;
            const eta = speed > 0 ? remainingBytes / speed : 0; // seconds

            // Attach speed and ETA to the event (for components to use)
            (event as any).uploadSpeed = speed;
            (event as any).eta = eta;

            lastProgressTime = now;
            lastProgressBytes = event.loaded;
          }

          // Scale upload progress to 85% to leave room for server processing
          const uploadPercent = Math.round(85 * event.loaded / event.total);
          (event as any).scaledProgress = uploadPercent;
          (event as any).stage = 'uploading';
          (event as any).statusMessage = 'Envoi du fichier...';
        } else if (event.type === HttpEventType.Response) {
          // Add processing stage when server responds
          (event as any).scaledProgress = 100;
          (event as any).stage = 'completed';
          (event as any).statusMessage = 'Upload terminé!';
        }
      }),
      timeout(30 * 60 * 1000), // 30 minutes timeout using RxJS timeout operator
      retry({
        count: this.maxRetries,
        delay: (_, retryCount) => timer(2**retryCount * 1000)
      }),
      catchError(this.handleError.bind(this))
    );
  }

  private multipartUpload(
    file: File,
    entityName: string,
    id: string,
    isCover: boolean,
    fileType?: 'teaser' | 'cover-image' | 'booklet' | 'booklet-thumbnail'
  ): Observable<HttpEvent<any>> {
    const uploadId = this.generateUploadId();
    const totalChunks = Math.ceil(file.size / this.chunkSize);

    // Create upload session
    const session: UploadSession = {
      uploadId,
      fileName: file.name,
      fileSize: file.size,
      chunkSize: this.chunkSize,
      totalChunks,
      uploadedChunks: [],
      status: 'preparing',
      entityName,
      entityId: id,
      isCover,
      fileType,
      cancelSubject: new Subject<void>()
    };

    this.uploadSessions.set(uploadId, session);

    return new Observable(observer => {
      this.initializeMultipartUpload(session)
        .then(() => {
          session.status = 'uploading';
          return this.uploadChunks(file, session, observer);
        })
        .then(() => {
          session.status = 'completed';
          observer.next({
            type: HttpEventType.Response,
            body: { success: true, uploadId },
            status: 200,
            statusText: 'OK',
            headers: new HttpHeaders(),
            url: session.uploadUrl
          } as any);
          observer.complete();
        })
        .catch(error => {
          session.status = 'error';
          observer.error(error);
        })
        .finally(() => {
          this.uploadSessions.delete(uploadId);
        });
    });
  }

  private async initializeMultipartUpload(session: UploadSession): Promise<void> {
    // For contents without ID, we need to create content first or use a different approach
    if ((session.entityName === 'contents' || session.entityName === 'formation-contents') && (!session.entityId || session.entityId.trim().length === 0)) {
      throw new Error('Content ID is required for multipart upload. Please create content first or use regular upload.');
    }

    const urlParts = [this.apiUrl, session.entityName];
    if (session.entityId.trim().length > 0) {
      urlParts.push(session.entityId);
    }
    urlParts.push('initiate-multipart-upload');

    const body: any = {
      fileName: session.fileName,
      fileSize: session.fileSize,
      chunkSize: session.chunkSize,
      totalChunks: session.totalChunks,
      isCover: session.isCover
    };

    // Add fileType for formations
    if (session.entityName === 'formations' && session.fileType) {
      body.fileType = session.fileType;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<{ uploadUrl: string; uploadId: string }>(
          urlParts.join('/'),
          body
        )
      );

      if (response) {
        session.uploadUrl = response.uploadUrl;
        session.uploadId = response.uploadId;
      }
    } catch (error) {
      throw new Error(`Failed to initialize multipart upload: ${error}`);
    }
  }

  private async uploadChunks(
    file: File,
    session: UploadSession,
    observer: any
  ): Promise<void> {
    let uploadedBytes = 0;

    for (let chunkNumber = 1; chunkNumber <= session.totalChunks; chunkNumber++) {
      // Check if upload was cancelled
      if (session.status === 'cancelled') {
        throw new Error('Upload cancelled');
      }

      // Check if upload was paused
      while (session.status === 'paused') {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const start = (chunkNumber - 1) * session.chunkSize;
      const end = Math.min(start + session.chunkSize, file.size);
      const chunk = file.slice(start, end);

      try {
        await this.uploadChunk(chunk, chunkNumber, session);
        uploadedBytes += chunk.size;

        // Calculate progress (scale to 85% to leave room for finalization)
        const uploadProgress = Math.round(85 * uploadedBytes / file.size);
        const isLastChunk = chunkNumber === session.totalChunks;

        // Report progress with status message
        observer.next({
          type: HttpEventType.UploadProgress,
          loaded: uploadedBytes,
          total: file.size,
          scaledProgress: uploadProgress,
          stage: isLastChunk ? 'finalizing' : 'uploading',
          statusMessage: isLastChunk
            ? 'Assemblage des morceaux...'
            : `Upload du morceau ${chunkNumber}/${session.totalChunks}...`
        });

      } catch (error) {
        // Retry failed chunk
        let retryCount = 0;
        while (retryCount < this.maxRetries) {
          try {
            await new Promise(resolve => setTimeout(resolve, 2**retryCount * 1000));
            await this.uploadChunk(chunk, chunkNumber, session);
            uploadedBytes += chunk.size;
            break;
          } catch (retryError) {
            retryCount++;
            if (retryCount >= this.maxRetries) {
              throw new Error(`Failed to upload chunk ${chunkNumber} after ${this.maxRetries} retries: ${retryError}`);
            }
          }
        }
      }
    }

    // Show finalizing status
    observer.next({
      type: HttpEventType.UploadProgress,
      loaded: file.size,
      total: file.size,
      scaledProgress: 95,
      stage: 'finalizing',
      statusMessage: 'Finalisation de l\'upload...'
    });

    // Complete multipart upload
    await this.completeMultipartUpload(session);
  }

  private async uploadChunk(
    chunk: Blob,
    chunkNumber: number,
    session: UploadSession
  ): Promise<void> {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunkNumber', chunkNumber.toString());
    formData.append('uploadId', session.uploadId);

    // Add fileType for formations
    if (session.entityName === 'formations' && session.fileType) {
      formData.append('fileType', session.fileType);
    }

    const urlParts = [this.apiUrl, session.entityName];
    if (session.entityId.trim().length > 0) {
      urlParts.push(session.entityId);
    }
    urlParts.push('upload-chunk');

    try {
      const response = await firstValueFrom(
        this.http.post<{ etag: string }>(
          urlParts.join('/'),
          formData
        ).pipe(
          timeout(5 * 60 * 1000) // 5 minutes per chunk using RxJS timeout
        )
      );

      if (response) {
        session.uploadedChunks.push({
          chunkNumber,
          start: (chunkNumber - 1) * session.chunkSize,
          end: Math.min(chunkNumber * session.chunkSize, session.fileSize),
          size: chunk.size,
          uploadId: session.uploadId,
          etag: response.etag
        });
      }
    } catch (error) {
      throw new Error(`Failed to upload chunk ${chunkNumber}: ${error}`);
    }
  }

  private async completeMultipartUpload(session: UploadSession): Promise<void> {
    const urlParts = [this.apiUrl, session.entityName];
    if (session.entityId.trim().length > 0) {
      urlParts.push(session.entityId);
    }
    urlParts.push('complete-multipart-upload');

    const body: any = {
      uploadId: session.uploadId,
      parts: session.uploadedChunks.map(chunk => ({
        ETag: chunk.etag,
        PartNumber: chunk.chunkNumber
      }))
    };

    // Add fileType for formations
    if (session.entityName === 'formations' && session.fileType) {
      body.fileType = session.fileType;
    }

    try {
      await firstValueFrom(this.http.post(urlParts.join('/'), body));
    } catch (error) {
      throw new Error(`Failed to complete multipart upload: ${error}`);
    }
  }

  pauseUpload(uploadId: string): void {
    const session = this.uploadSessions.get(uploadId);
    if (session) {
      session.status = 'paused';
    }
  }

  resumeUpload(uploadId: string): Observable<HttpEvent<any>> {
    const session = this.uploadSessions.get(uploadId);
    if (!session) {
      return throwError(() => new Error('Upload session not found'));
    }

    session.status = 'uploading';

    return new Observable(observer => {
      // Resume from where we left off
      const uploadedBytes = session.uploadedChunks.reduce((total, chunk) => total + chunk.size, 0);

      observer.next({
        type: HttpEventType.UploadProgress,
        loaded: uploadedBytes,
        total: session.fileSize
      });

      // Continue upload would require file reference - this is a simplified version
      // In a real implementation, you'd need to store file chunks or re-request the file
      observer.next({
        type: HttpEventType.Response,
        body: { success: true, resumed: true },
        status: 200,
        statusText: 'OK',
        headers: new HttpHeaders(),
        url: session.uploadUrl
      } as any);

      observer.complete();
    });
  }

  cancelUpload(uploadId: string): void {
    const session = this.uploadSessions.get(uploadId);
    if (session) {
      session.status = 'cancelled';
      session.cancelSubject?.next();
      session.cancelSubject?.complete();

      // Abort multipart upload on server
      this.abortMultipartUpload(session).catch(error => {
        console.error('Failed to abort multipart upload:', error);
      });

      this.uploadSessions.delete(uploadId);
    }
  }

  private async abortMultipartUpload(session: UploadSession): Promise<void> {
    const urlParts = [this.apiUrl, session.entityName];
    if (session.entityId.trim().length > 0) {
      urlParts.push(session.entityId);
    }
    urlParts.push('abort-multipart-upload');

    try {
      await firstValueFrom(this.http.post(urlParts.join('/'), { uploadId: session.uploadId }));
    } catch (error) {
      console.error('Failed to abort multipart upload:', error);
    }
  }

  getUploadProgress(uploadId: string): number {
    const session = this.uploadSessions.get(uploadId);
    if (!session) return 0;

    const uploadedBytes = session.uploadedChunks.reduce((total, chunk) => total + chunk.size, 0);
    return Math.round((uploadedBytes / session.fileSize) * 100);
  }

  private generateUploadId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private getContentFileType(file: File): string {
    // Get file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    // Check MIME type first, then fallback to extension
    if (file.type.startsWith('video/') || ['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(fileExtension || '')) {
      return 'video';
    }
    if (file.type.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(fileExtension || '')) {
      return 'audio';
    }
    if (file.type === 'application/pdf' || fileExtension === 'pdf') {
      return 'pdf';
    }
    if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileExtension || '')) {
      return 'image';
    }
    if (['doc', 'docx', 'xls', 'xlsx', 'csv'].includes(fileExtension || '')) {
      return 'document';
    }

    // Default fallback
    return 'file';
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred during upload';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server error: ${error.status} - ${error.message}`;
    }

    console.error('Upload error:', error);
    return throwError(() => new Error(errorMessage));
  }
}